import React, { useState } from 'react';
import { Modal, Typography, Tabs, Space, Divider, Card, Tag, Button, Row, Col, Tooltip } from 'antd';
import {
    FileTextOutlined,
    FileExcelOutlined,
    DesktopOutlined,
    MobileOutlined,
    CloudUploadOutlined,
    CheckCircleFilled,
    InfoCircleOutlined,
    BankOutlined,
    RocketOutlined,
    SearchOutlined,
    ArrowRightOutlined,
    WhatsAppOutlined
} from '@ant-design/icons';
import { motion, AnimatePresence } from 'framer-motion';

const { Title, Paragraph, Text } = Typography;

export default function InstrucoesModal({ open, onClose }) {
    const [activeTab, setActiveTab] = useState('ofx');

    const bankInstructions = [
        { bank: 'Itaú', path: 'Menu > Conta Corrente > Extrato > Salvar em outros formatos > OFX', color: '#ec7000' },
        { bank: 'Bradesco', path: 'Saldos e Extratos > Extrato Mensal/Período > Salvar como arquivo > OFX', color: '#cc092f' },
        { bank: 'Banco do Brasil', path: 'Menu Principal > Extrato > Conta Corrente > Exportar > OFX (Money)', color: '#fcf800', textColor: '#0038a8' },
        { bank: 'Santander', path: 'Menu > Conta Corrente > Extrato > Exportar (OFX)', color: '#ec0000' },
        { bank: 'Nubank', path: 'App > Conta > Histórico > Exportar Extrato > OFX', color: '#8a05be' },
        { bank: 'Inter', path: 'Menu Lateral > Extrato > Selecione o Período > Exportar OFX', color: '#ff7a00' },
        { bank: 'C6 Bank', path: 'App > Exibir Extrato > Exportar > Escolha o Período > OFX', color: '#000000' },
        { bank: 'BTG Pactual', path: 'Menu Transacional > Extrato > Exportar > OFX', color: '#002a4e' },
    ];

    const containerVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    const tabItems = [
        {
            key: 'ofx',
            label: (
                <Space>
                    <FileTextOutlined />
                    <span>Importação OFX</span>
                </Space>
            ),
            children: (
                <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ padding: '8px 4px' }}>
                    <div className="modern-help-hero">
                        <Title level={4} style={{ marginBottom: 8, color: '#1e293b' }}>
                            <RocketOutlined style={{ color: '#3b82f6', marginRight: 12 }} />
                            Acelere sua Conciliação Bancária
                        </Title>
                        <Paragraph type="secondary" style={{ fontSize: '15px' }}>
                            O formato <b>OFX</b> é o padrão ouro para contabilidade. Ele elimina erros de digitação e garante que seu saldo bata exatamente com o banco.
                        </Paragraph>
                    </div>

                    <Row gutter={[20, 20]} style={{ marginTop: 24 }}>
                        <Col xs={24} sm={12}>
                            <Card className="modern-instruction-card desktop">
                                <DesktopOutlined className="step-icon" />
                                <Title level={5}>Pelo Computador</Title>
                                <Paragraph type="secondary" style={{ fontSize: '13px', margin: 0 }}>
                                    Acesse o Internet Banking, exporte seu extrato em formato <b>OFX (Money)</b>. Salve na sua pasta de downloads e arraste para o portal.
                                </Paragraph>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Card className="modern-instruction-card mobile">
                                <MobileOutlined className="step-icon" />
                                <Title level={5}>Pelo Celular</Title>
                                <Paragraph type="secondary" style={{ fontSize: '13px', margin: 0 }}>
                                    No app do banco, use a opção "Exportar Extrato" e envie para o seu e-mail ou salve no dispositivo. O portal é 100% compatível com mobile.
                                </Paragraph>
                            </Card>
                        </Col>
                    </Row>

                    <Divider orientation="left" style={{ marginTop: 32 }}>
                        <SearchOutlined style={{ marginRight: 8 }} />
                        Caminhos Rápidos por Instituição
                    </Divider>

                    <div className="bank-path-list">
                        {bankInstructions.map((item, idx) => (
                            <div key={idx} className="bank-path-item" style={{ borderLeftColor: item.color }}>
                                <div className="bank-badge" style={{ backgroundColor: item.color, color: item.textColor || '#fff' }}>
                                    {item.bank}
                                </div>
                                <div className="bank-guide-text">
                                    {item.path.split(' > ').map((step, sIdx, arr) => (
                                        <React.Fragment key={sIdx}>
                                            <Text strong={sIdx === arr.length - 1} style={{ fontSize: '13px' }}>{step}</Text>
                                            {sIdx < arr.length - 1 && <ArrowRightOutlined style={{ fontSize: '10px', margin: '0 6px', color: '#94a3b8' }} />}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="portal-action-hint">
                        <Space align="start">
                            <CloudUploadOutlined style={{ fontSize: 24, color: '#1677ff', marginTop: 4 }} />
                            <div>
                                <Text strong style={{ fontSize: '16px', display: 'block' }}>Como subir no portal?</Text>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    Vá em <b>Financeiro &gt; Lançamentos</b>, clique em <b>Importar</b> e selecione seu arquivo. O sistema reconhecerá automaticamente datas e valores.
                                </Text>
                            </div>
                        </Space>
                    </div>
                </motion.div>
            ),
        },
        {
            key: 'excel',
            label: (
                <Space>
                    <FileExcelOutlined />
                    <span>Planilha de Dados</span>
                </Space>
            ),
            children: (
                <motion.div variants={containerVariants} initial="hidden" animate="visible" style={{ padding: '8px 4px' }}>
                    <Title level={4}>Importação Manual via Excel</Title>
                    <Paragraph type="secondary">
                        Caso não consiga o OFX, você pode usar uma planilha simples. O sistema espera que os dados comecem na <b>primeira linha</b>, sem cabeçalhos.
                    </Paragraph>

                    <div className="excel-structure-guide">
                        {[
                            { col: 'A', name: 'Data do Evento', format: 'DD/MM/AAAA', example: '15/05/2024' },
                            { col: 'B', name: 'Descrição / Histórico', format: 'Texto livre', example: 'Pagamento Fornecedor XYZ' },
                            { col: 'C', name: 'Valor da Transação', format: 'Número (0,00)', example: '1.250,50' },
                            { col: 'D', name: 'Tipo de Lançamento', format: 'Letra Única', example: 'D (Débito) ou C (Crédito)' },
                        ].map((field, idx) => (
                            <div key={idx} className="excel-field-row">
                                <div className="excel-col-badge">{field.col}</div>
                                <div className="excel-field-info">
                                    <Text strong>{field.name}</Text>
                                    <Tag color="blue" style={{ marginLeft: 8 }}>{field.format}</Tag>
                                </div>
                                <div className="excel-field-example">
                                    <Text code>{field.example}</Text>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ marginTop: 24, padding: '16px', borderRadius: '12px', background: '#fff7e6', border: '1px solid #ffd666', display: 'flex', gap: '12px' }}>
                        <InfoCircleOutlined style={{ color: '#faad14', fontSize: '18px', marginTop: '2px' }} />
                        <div>
                            <Text strong style={{ color: '#874d00' }}>Evite Erros Comuns:</Text>
                            <ul style={{ paddingLeft: '18px', margin: '8px 0 0 0', color: '#874d00', fontSize: '13px' }}>
                                <li>Não utilize linhas de título ou totais na planilha.</li>
                                <li>Certifique-se de que os valores não contenham símbolos de moeda (R$).</li>
                                <li>As datas devem estar no formado brasileiro (dia/mês/ano).</li>
                            </ul>
                        </div>
                    </div>
                </motion.div>
            )
        }
    ];

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="modal-title-icon">
                        <RocketOutlined />
                    </div>
                    <div>
                        <div style={{ fontWeight: 800, fontSize: '18px', lineHeight: 1 }}>Central de Importação</div>
                        <div style={{ fontSize: '11px', fontWeight: 500, color: '#64748b', marginTop: 4 }}>Dicas e guias rápidos para seus lançamentos</div>
                    </div>
                </div>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={720}
            centered
            className="premium-instruction-modal"
            style={{ maxWidth: '95vw', top: window.innerWidth < 576 ? 20 : undefined }}
            bodyStyle={{ padding: window.innerWidth < 576 ? '0 16px 20px 16px' : '0 24px 24px 24px' }}
        >
            <style>{`
                .premium-instruction-modal .ant-modal-content {
                    border-radius: 20px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                .premium-instruction-modal .ant-typography {
                    white-space: normal !important;
                    word-break: break-word !important;
                    display: block !important;
                    overflow: visible !important;
                    -webkit-line-clamp: initial !important;
                    -webkit-box-orient: vertical !important;
                    text-overflow: clip !important;
                }
                .premium-instruction-modal .ant-modal-header {
                    padding: 24px;
                    border-bottom: 1px solid #f1f5f9;
                    margin-bottom: 0;
                }
                .modal-title-icon {
                    width: 40px;
                    height: 40px;
                    background: linear-gradient(135deg, #1677ff 0%, #00d2ff 100%);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 20px;
                    box-shadow: 0 4px 12px rgba(22, 119, 255, 0.3);
                }
                .modern-help-hero {
                    background: #f8fafc;
                    padding: 20px;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                }
                .modern-instruction-card {
                    border-radius: 16px;
                    border: 1px solid #f1f5f9;
                    background: #ffffff;
                    transition: all 0.3s ease;
                    height: 100%;
                }
                .modern-instruction-card:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                    transform: translateY(-2px);
                }
                .modern-instruction-card.desktop { border-top: 4px solid #3b82f6; }
                .modern-instruction-card.mobile { border-top: 4px solid #10b981; }
                .step-icon {
                    font-size: 24px;
                    margin-bottom: 16px;
                    color: #334155;
                }
                .bank-path-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }
                .bank-path-item {
                    display: flex;
                    align-items: center;
                    padding: 12px 16px;
                    background: #ffffff;
                    border: 1px solid #f1f5f9;
                    border-left: 4px solid #cbd5e1;
                    border-radius: 8px;
                    gap: 16px;
                }
                .bank-badge {
                    min-width: 80px;
                    text-align: center;
                    padding: 4px 8px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 800;
                    text-transform: uppercase;
                }
                .bank-guide-text {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    flex-wrap: wrap;
                }
                .portal-action-hint {
                    margin-top: 32px;
                    background: linear-gradient(to right, #eff6ff, #f0f9ff);
                    padding: 20px;
                    border-radius: 16px;
                    border: 1px solid #bfdbfe;
                }
                .excel-structure-guide {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    margin-top: 20px;
                }
                .excel-field-row {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    background: #f8fafc;
                    padding: 12px 16px;
                    border-radius: 10px;
                    border: 1px solid #e2e8f0;
                }
                .excel-col-badge {
                    width: 32px;
                    height: 32px;
                    background: #1e293b;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 14px;
                }
                .excel-field-info {
                    flex: 1;
                }
                .excel-field-example {
                    text-align: right;
                }

                @media (max-width: 576px) {
                    .premium-instruction-modal .ant-modal-header {
                        padding: 16px;
                    }
                    .modal-title-icon {
                        width: 32px;
                        height: 32px;
                        font-size: 16px;
                    }
                    .premium-instruction-modal .ant-modal-body {
                        padding: 0 16px 16px 16px !important;
                    }
                    .modern-help-hero {
                        padding: 16px;
                    }
                    .bank-path-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }
                    .bank-badge {
                        width: 100%;
                    }
                    .excel-field-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    .excel-field-example {
                        width: 100%;
                        text-align: left;
                    }
                    .modern-instruction-card {
                        height: auto !important;
                    }
                    .portal-action-hint {
                        padding: 16px;
                    }
                    .ant-divider-inner-text {
                        font-size: 13px !important;
                        padding: 0 8px !important;
                    }
                }
            `}</style>

            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                indicator={{ size: (origin) => origin - 20, align: 'center' }}
                style={{ marginTop: 12 }}
            />

            <Divider style={{ margin: '24px 0 12px 0' }} />
            <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
                    Ainda precisando de ajuda?
                </Text>
                <Button
                    type="primary"
                    icon={<WhatsAppOutlined />}
                    style={{ backgroundColor: '#25D366', borderColor: '#25D366' }}
                    href="https://wa.me/5508000420627?text=Olá!%20Preciso%20de%20ajuda%20com%20a%20importação%20de%20arquivos%20financeiros%20no%20portal."
                    target="_blank"
                >
                    Falar com Especialista
                </Button>
            </div>
        </Modal>
    );
}
